import classNames from 'classnames'
import React, { ComponentType, ReactNode } from 'react'

import { Card } from '../../card/card.js'
import { GridColumn } from '../../grid/grid-column.js'
import { Grid } from '../../grid/grid.js'
import { IconProps } from '../../icon/icon.js'

export type CheckInputAttributes = Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'type' | 'checked' | 'defaultChecked' | 'value' | 'onChange' | 'disabled' | 'aria-invalid'
>

export interface CheckCardProps {
    icon?: ComponentType<IconProps>
    id: string
    label: ReactNode
    value: string
    error?: string
    checked?: boolean
    children?: ReactNode
    className?: string
    description?: string
    input?: CheckInputAttributes
    onChange?: (checked: boolean) => void
}

export const CheckCard = (props: CheckCardProps): JSX.Element => {
    const { input, checked, children, value, id } = props

    const className: string = classNames('check-card', props.className, props.error && 'check-card--error')

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.currentTarget.checked

        if (props.onChange) {
            props.onChange(nextValue)
        }
    }

    return (
        <Card className={className} variant={checked ? 'secondary' : undefined}>
            <input
                {...input}
                type="checkbox"
                checked={checked}
                value={value}
                onChange={onChange}
                id={id}
                className="check-card__input"
            />
            <label htmlFor={id} className="check-card__label">
                <Grid gutter="mobile" noWrap={true}>
                    {props.icon && (
                        <GridColumn width={['min-xs']}>
                            <props.icon className="check-card__icon" />
                        </GridColumn>
                    )}
                    <GridColumn width={['max-xs']}>
                        <span className="check-card__text">{props.label}</span>
                        {props.description && <p className="check-card__description">{props.description}</p>}
                    </GridColumn>
                </Grid>
            </label>
            {props.error && <p className="check-card__error">{props.error}</p>}
            <span className="check-card__indicator" />
            {checked && children && <div className="check-card__children">{children}</div>}
        </Card>
    )
}
